@use "../../../styles/breakpoint.module.scss" as *;

.heading {
    margin-bottom: var(--spacer-40);
    font: var(--font-h-40);
    text-align: center;
}

// Section eyebrow above each box (e.g. "Cooking Set", "Refrigeration Set",
// "1 Additional Appliance", "Accessories"). Sits outside the visual box
// container per the design.
.sectionEyebrow {
    margin: var(--spacer-32) 0 var(--spacer-16);
    font: var(--font-p-20);
    text-align: left;
}

// Side-by-side row of boxes (Cooking Set has two; Refrigeration Set has
// two). Stacks on tablet & below.
.boxRow {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--spacer-24);
    text-align: left;

    @media ($below-tablet) {
        grid-template-columns: 1fr;
    }
}

// Visual container with a faint tinted background, holding one or more
// groups with their internal layout.
.box {
    padding: var(--spacer-32);
    background-color: var(--white);
    text-align: left;

    @media ($below-mobile) {
        padding: var(--spacer-24);
    }
}

// 2-column internal layout for the Cooking Set's right box (Rangetops /
// Cooktops on the left, Built-in Ovens on the right).
.boxColumns {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--spacer-24);

    @media ($below-mobile) {
        grid-template-columns: 1fr;
    }
}

// Multi-column internal layout for sub-groups inside a single group
// (e.g. Ranges' DUAL FUEL / INDUCTION / GAS, or French Door split).
.subGroupColumns {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--spacer-24);

    @media ($below-mobile) {
        grid-template-columns: 1fr;
    }
}

// Vertical stack of multiple subgroups inside a single grid cell of
// `.subGroupColumns` (e.g. Induction + Gas under Ranges' second column).
// Lives at this level so sub-groups at the top of a stack get the same
// spacing treatment as ones in the middle.
.subGroupStack {
    display: flex;
    flex-direction: column;
    gap: var(--spacer-16);
}

// Four-column layout for the "1 Additional Appliance" box.
.fourColumns {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: var(--spacer-24);

    @media ($below-tablet) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media ($below-mobile) {
        grid-template-columns: 1fr;
    }
}

// Accessories box: Refrigeration Accessories occupies 3 column-widths
// (21 SKUs flow into 3 sub-columns), Cooking Accessories takes 1.
.accessoriesLayout {
    display: grid;
    grid-template-columns: 3fr 1fr;
    gap: var(--spacer-32);

    .skuList {
        display: block;
        column-count: 3;
        column-gap: var(--spacer-24);
    }

    .skuItem {
        margin-bottom: var(--spacer-4);
        break-inside: avoid;
    }

    > :last-child .skuList {
        column-count: 1;
    }

    @media ($below-tablet) {
        grid-template-columns: 1fr;

        .skuList,
        > :last-child .skuList {
            column-count: 2;
        }
    }

    @media ($below-mobile) {
        .skuList,
        > :last-child .skuList {
            column-count: 1;
        }
    }
}

// Flex column with gap so the group title and its children — and any
// stacked subgroups directly inside — get uniform spacing without
// per-sibling margin tricks.
.group {
    display: flex;
    flex-direction: column;
    gap: var(--spacer-16);
}

.groupTitle {
    margin: 0;
    font: var(--font-h-24);
}

.subGroup {
    display: flex;
    flex-direction: column;
    gap: var(--spacer-8);
}

.subGroupLabel {
    margin: 0;
    font: var(--font-eyebrow-12);
    letter-spacing: 0.1em;
    text-transform: uppercase;
}

.skuList {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--spacer-4);
}

.skuItem {
    display: flex;
    gap: var(--spacer-8);
    font: var(--font-p-14);
    break-inside: avoid;
}

.skuCheckbox {
    flex: 0 0 auto;
    width: 16px;
    height: 16px;
    margin-top: 2px;
    border: 1px solid currentcolor;
}

.skuCode {
    letter-spacing: 0.04em;
}

// "—— or ——" between Rangetops and Cooktops in the Cooking Set's right
// box, separating the two halves of the qualifying-package alternative.
.orDivider {
    display: flex;
    align-items: center;
    gap: var(--spacer-12);
    margin: var(--spacer-16) 0;
    font: var(--font-p-14);

    &::before,
    &::after {
        content: "";
        flex: 1;
        height: 1px;
        background-color: var(--pecan);
    }
}

.terms {
    margin-top: var(--spacer-40);
    padding-top: var(--spacer-24);
    border-top: 1px solid var(--gray-300);
    text-align: left;
    font: var(--font-p-14);
    line-height: 1.5;

    p {
        margin: 0 0 var(--spacer-12);
    }
}

.termsHeading {
    margin-bottom: var(--spacer-16);
    font: var(--font-eyebrow-14);
    letter-spacing: 0.1em;
    text-transform: uppercase;
}

.copyright {
    margin-top: var(--spacer-16);
}
